import configureStore from "configureStore";
import { TanStackDevtools } from "@tanstack/react-devtools";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtoolsPanel } from "@tanstack/react-query-devtools";
import i18n from "i18n";
import "index.css";
import { POSTHOG_API_KEY, POSTHOG_OPTIONS } from "lib/tracker";
import { PostHogProvider } from "posthog-js/react";
import * as Sentry from "@sentry/react";
import React from "react";
import { createRoot } from "react-dom/client";
import { I18nextProvider } from "react-i18next";
import { AuthProvider } from "react-oidc-context";
import { Provider } from "react-redux";
import { SWRConfig } from "swr";
import { APIContext, configureApi } from "utils/api";
import { API2Context, configureApi2 } from "utils/api2";
import { getAPIUrl } from "utils/getApiUrl";
import { getOIDCConfig } from "utils/oidc";
import { logStartupBanner } from "utils/startupBanner";
import app from "./app";
import { handleSWRError, reportError } from "./lib/sentry";
import { ParaglideProvider } from "./ParaglideProvider";

async function startApp() {
	try {
		if (!(window as any).Cypress) {
			logStartupBanner();
		}
		const initialState = (window as any).__INITIAL_REDUX_STATE__;
		const api = configureApi(getAPIUrl().api);
		const api2 = configureApi2(getAPIUrl().api2);
		const store = configureStore(initialState, { api, i18n });

		if ((window as any).Cypress) {
			// give cypress e2e access to store
			(window as any).store = store;
		}
		// Render Setup
		const MOUNT_NODE = document.getElementById("root");
		const root = createRoot(MOUNT_NODE!, {
			onCaughtError: Sentry.reactErrorHandler(),
			onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
				console.warn("Uncaught error", error, errorInfo.componentStack);
			}),
			onRecoverableError: Sentry.reactErrorHandler(),
		});
		const queryClient = new QueryClient({
			defaultOptions: {
				mutations: {
					onError: (error, params) => {
						reportError(error, false, params);
					},
				},
			},
		});
		const renderApp = (AppElement: typeof import("./app").default): void => {
			root.render(
				<React.StrictMode>
					<PostHogProvider apiKey={POSTHOG_API_KEY} options={POSTHOG_OPTIONS}>
						<AuthProvider {...getOIDCConfig()}>
							<APIContext.Provider value={api}>
								<API2Context.Provider value={api2}>
									<QueryClientProvider client={queryClient}>
										<SWRConfig value={{ onError: handleSWRError }}>
											<Provider store={store}>
												<I18nextProvider i18n={i18n}>
													<ParaglideProvider>
														<AppElement />
													</ParaglideProvider>
												</I18nextProvider>
											</Provider>
										</SWRConfig>
										<TanStackDevtools
											plugins={[
												{
													name: "TanStack Query",
													render: <ReactQueryDevtoolsPanel />,
												},
											]}
										/>
									</QueryClientProvider>
								</API2Context.Provider>
							</APIContext.Provider>
						</AuthProvider>
					</PostHogProvider>
				</React.StrictMode>,
			);
		};
		renderApp(app);
	} catch (err) {
		reportError(err);
	}
}

if (window.location.pathname === "/silent_renew") {
	const MOUNT_NODE = document.getElementById("root");
	const root = createRoot(MOUNT_NODE!);
	root.render(
		<AuthProvider {...getOIDCConfig()}>
			<div>silent renew</div>
		</AuthProvider>,
	);
} else {
	startApp();
}
